import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { ChartContainer, ChartTooltip } from "@/components/chart";
import type { JiraMetricsScatterPoint } from "@/http/api-types";
import { CartesianGrid, Scatter, ScatterChart, XAxis, YAxis } from "recharts";
import { EmptyState } from "./empty-state";
import { formatDays, secondsToDays } from "./formatters";

type ScatterPoint = {
  ticket_key: string;
  review_days: number;
  cycle_days: number;
};

function ScatterTooltipContent({ active, payload }: { active?: boolean; payload?: Array<{ payload?: ScatterPoint }> }) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;
  const ticketKey = point?.ticket_key || "-";

  return (
    <div className="grid min-w-[12rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Ticket</span>
        <span className="font-mono font-medium text-foreground">{ticketKey}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Review</span>
        <span className="font-mono font-medium text-foreground">{formatDays(point?.review_days)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground">Cycle</span>
        <span className="font-mono font-medium text-foreground">{formatDays(point?.cycle_days)}</span>
      </div>
    </div>
  );
}

export function ReviewVsCycleScatterChart({ data }: { data: JiraMetricsScatterPoint[] }) {
  const chartData = data.map(point => ({
    ticket_key: point.ticket_key,
    review_days: secondsToDays(point.review_seconds),
    cycle_days: secondsToDays(point.cycle_seconds),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Time vs Cycle Time (Scatter)</CardTitle>
        <CardDescription>
          Each dot is one done ticket. X axis is review time, Y axis is total cycle time. A steeper slope means review
          is driving delays.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <EmptyState message="No done tickets with cycle-time data in selected range. Try a wider date range." />
        ) : (
          <ChartContainer
            className="h-72 w-full"
            config={{ cycle_days: { label: "Cycle (workdays)", color: "#ef4444" } }}
          >
            <ScatterChart>
              <CartesianGrid />
              <XAxis
                type="number"
                dataKey="review_days"
                name="Review Workdays"
                domain={[0, "auto"]}
                tickFormatter={value => `${Number(value).toFixed(1)}d`}
              />
              <YAxis
                type="number"
                dataKey="cycle_days"
                name="Cycle Workdays"
                domain={[0, "auto"]}
                tickFormatter={value => `${Number(value).toFixed(1)}d`}
              />
              <ChartTooltip content={<ScatterTooltipContent />} />
              <Scatter data={chartData} fill="var(--color-cycle_days)" />
            </ScatterChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
